import { useEffect } from "react";

/**
 * PageSeo.
 *
 * Declarative <head> manager for any single route. Sets <title>, meta
 * description, canonical, Open Graph, Twitter card, optional robots
 * directive, and any number of JSON-LD payloads. Every node it adds
 * is tagged with `data-am-page-seo` so the cleanup pass on unmount
 * removes the tags this page injected without touching the base
 * Organization / Person / Book / WebSite graph that lives in
 * index.html.
 *
 * Rule: every consumer must pass plain ASCII copy (no em / en dashes).
 */
const TAG_ATTR = "data-am-page-seo";

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    el.setAttribute(TAG_ATTR, "1");
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    if (!el.getAttribute(TAG_ATTR)) el.setAttribute(TAG_ATTR, "1");
  }
};

const upsertLink = (rel, href) => {
  // Remove ANY existing link[rel] not owned by us so we end up with a
  // single canonical / alternate per page. The base index.html ships
  // a canonical to "/"; we replace it on every route.
  const existing = document.head.querySelectorAll(`link[rel="${rel}"]`);
  existing.forEach((n) => {
    if (!n.getAttribute(TAG_ATTR)) n.remove();
  });
  let el = document.head.querySelector(`link[rel="${rel}"][${TAG_ATTR}]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    el.setAttribute(TAG_ATTR, "1");
    document.head.appendChild(el);
  }
  el.href = href;
};

const injectJsonLd = (id, payload) => {
  let el = document.head.querySelector(`script[data-am-jsonld-page="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-am-jsonld-page", id);
    el.setAttribute(TAG_ATTR, "1");
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
};

const HOME_CANONICAL = "https://authentic-momentum.com/";

const cleanup = () => {
  document.head.querySelectorAll(`[${TAG_ATTR}]`).forEach((n) => n.remove());
  // After tearing down a per-page PageSeo, restore the homepage
  // canonical so SPA navigation back to / never lands without one.
  const remaining = document.head.querySelectorAll('link[rel="canonical"]');
  if (remaining.length === 0) {
    const el = document.createElement("link");
    el.rel = "canonical";
    el.href = HOME_CANONICAL;
    document.head.appendChild(el);
  }
};

const PageSeo = ({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage,
  twitterCard = "summary_large_image",
  jsonLd = [],
}) => {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      upsertMeta('meta[name="description"]', { name: "description", content: description });
    }
    if (canonical) {
      upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
      upsertLink("canonical", canonical);
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
      upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    }
    if (description) {
      upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
      upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    }
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: ogType });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: twitterCard });
    if (ogImage) {
      upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
      upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
    }
    (jsonLd || []).forEach((payload, i) => {
      injectJsonLd(`page-${i}`, payload);
    });
    return cleanup;
  }, [title, description, canonical, ogType, ogImage, twitterCard, jsonLd]);

  return null;
};

export default PageSeo;
